import { useState, useCallback, useMemo, useRef, useEffect, type MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { PageBanner } from "@/components/reusable/PageBanner";
import { Button, AlertMessage } from "@/components/reusable";
import { SearchableSelect } from "@/components/mapping/SearchableSelect";
import { MappingStatusBadge } from "@/components/mapping/MappingStatusBadge";
import { TargetColumnTooltip } from "@/components/mapping/TargetColumnTooltip";
import { FilePreviewModal } from "@/components/mapping/FilePreviewModal";
import { RegionMappingStepper } from "@/components/mapping/RegionMappingStepper";
import { Spinner } from "@/components/reusable/Spinner";
import { useMappingState } from "@/hooks/useMappingState";
import { useUpload } from "@/hooks/useUpload";
import { useProcessing } from "@/hooks/useProcessing";
import { useTelemetry } from "@/hooks/useTelemetry";
import {
  resolveExtractionMappingTarget,
  mappingRegionTableLabelKey,
  mappingRegionTableLabelParts,
} from "@/lib/mappingRegions";
import {
  createKnownMappings,
  applyMappings,
  fetchMappingProgress,
  saveMappingSnapshot,
} from "@/services/api";
import { cn } from "@/lib/utils";
import {
  buildFileMappedDqxPayload,
  requireDqxRunFromApply,
  type MappingWorkbenchDqxRun,
} from "@/lib/mappingWorkbenchDqx";
import { STATUS_SORT_ORDER } from "@/lib/schema";
import type { SchemaLookups } from "@/lib/schema";
import type {
  MappingRow,
  MappingSortConfig,
  MappingError,
  KnownMapping,
  SelectOption,
  SchemaField,
} from "@/types";

// ── Constants ──────────────────────────────────────────────

const rowVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
};

// MANDATORY_TARGET_IDS is now derived from schema lookups inside MappingPageInner

// ── Rich option builders ──────────────────────────────────

/** Build SelectOption[] for the source column dropdown (mandatory tab) */
function buildSourceOptions(sourceColumns: string[]): SelectOption[] {
  return sourceColumns.map((col) => ({ value: col, label: col }));
}

/** Build SelectOption[] for the target field dropdown (additional tab).
 * API suggestion order is preserved: `row.suggestions` (field ids) first, then remaining fields in schema order. */
function buildAdditionalTargetOptions(
  row: MappingRow,
  additionalFields: SchemaField[]
): SelectOption[] {
  const byId = new Map(additionalFields.map((f) => [f.id, f]));
  const seen = new Set<string>();
  const ordered: SelectOption[] = [];

  for (const id of row.suggestions) {
    const f = byId.get(id);
    if (f && !seen.has(f.id)) {
      seen.add(f.id);
      ordered.push({
        value: f.fieldName,
        label: f.fieldName,
        description: f.definition,
      });
    }
  }

  for (const f of additionalFields) {
    if (!seen.has(f.id)) {
      seen.add(f.id);
      ordered.push({
        value: f.fieldName,
        label: f.fieldName,
        description: f.definition,
      });
    }
  }

  return ordered;
}

// ── Sort Arrow ─────────────────────────────────────────────

function SortArrow({ active, direction }: { active: boolean; direction: "asc" | "desc" }) {
  if (!active) {
    return (
      <svg
        className="w-3 h-3 text-current opacity-40"
        viewBox="0 0 10 14"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M5 1.5 L2 4.5 M5 1.5 L8 4.5" />
        <path d="M5 12.5 L2 9.5 M5 12.5 L8 9.5" />
      </svg>
    );
  }
  return (
    <svg
      className="w-3 h-3 text-current"
      viewBox="0 0 10 10"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {direction === "asc" ? (
        <path d="M5 1 L5 9 M2 4 L5 1 L8 4" />
      ) : (
        <path d="M5 1 L5 9 M2 6 L5 9 L8 6" />
      )}
    </svg>
  );
}

// ── Table Header ───────────────────────────────────────────

type SortField = MappingSortConfig["field"];

const SORTABLE_COLUMNS: { field: SortField; label: string; className?: string }[] = [
  { field: "sourceColumn", label: "Source Column" },
  { field: "targetColumn", label: "Target Column" },
  { field: "status", label: "Mapping Status" },
  { field: "confidence", label: "Confidence", className: "justify-center" },
];

function TableHeader({
  sort,
  onSort,
  onSelectAll,
  onDeselectAll,
  approvedCount,
  approvableCount,
  isMandatoryTab,
}: {
  sort: MappingSortConfig;
  onSort: (field: SortField) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  approvedCount: number;
  approvableCount: number;
  isMandatoryTab: boolean;
}) {
  const allSelected = approvableCount > 0 && approvedCount >= approvableCount;

  return (
    <div className="sticky top-[45px] z-10 grid grid-cols-[1fr_1fr_160px_100px_80px] gap-3 px-4 py-2.5 bg-[#e6fbef] border-b border-[var(--color-brand-primary)]/20">
      {SORTABLE_COLUMNS.map((col) => {
        const isActive = sort.field === col.field;
        return (
          <button
            key={col.field}
            type="button"
            onClick={() => onSort(col.field)}
            className={cn(
              "flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider transition-colors duration-150 group text-left cursor-pointer",
              isActive
                ? "text-[var(--color-brand-primary-dark)]"
                : "text-[var(--color-brand-primary-dark)]/70 hover:text-[var(--color-brand-primary-dark)]",
              col.className
            )}
          >
            {col.label}
            <SortArrow active={isActive} direction={sort.direction} />
          </button>
        );
      })}
      <div className="flex items-center justify-center">
        {isMandatoryTab ? (
          <span className="text-[10px] font-medium text-[var(--color-brand-primary-dark)]/50 uppercase tracking-wide select-none">
            Required
          </span>
        ) : (
          <button
            type="button"
            onClick={allSelected ? onDeselectAll : onSelectAll}
            className="text-xs font-semibold text-[var(--color-brand-primary-dark)] hover:text-[var(--color-brand-primary)] underline underline-offset-2 transition-colors cursor-pointer"
          >
            {allSelected ? "Deselect All" : "Select All"}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Mapping Row ────────────────────────────────────────────

function MappingRowComponent({
  row,
  index,
  isMandatoryTab,
  sourceOptions,
  targetOptions,
  hasError,
  errorMessages,
  onFieldChange,
  onToggleApproval,
  schema,
}: {
  row: MappingRow;
  index: number;
  isMandatoryTab: boolean;
  sourceOptions: SelectOption[];
  targetOptions: SelectOption[];
  hasError: boolean;
  errorMessages: string[];
  onFieldChange: (option: SelectOption | null) => void;
  onToggleApproval: () => void;
  schema: SchemaLookups;
}) {
  const targetField = row.targetFieldId ? schema.byId.get(row.targetFieldId) : undefined;
  const targetDisplayName = schema.getFieldName(row.targetFieldId);

  // A row is complete when it has BOTH source and target
  const isComplete = !!row.sourceColumn && !!row.targetFieldId;

  // Mandatory tab rows that are complete are always approved and locked
  const isMandatoryLocked = isMandatoryTab && isComplete;

  const isCheckboxDisabled = isMandatoryLocked || !isComplete || hasError;
  const [showCheckboxTip, setShowCheckboxTip] = useState(false);

  let disabledReason = "";
  if (isMandatoryLocked) {
    disabledReason = "Mandatory fields are always included";
  } else if (hasError && errorMessages.length > 0) {
    disabledReason = `Resolve error before approving: ${errorMessages[0]}`;
  } else if (!isComplete) {
    disabledReason = isMandatoryTab
      ? "Select a source column before approving"
      : "Select a target field before approving";
  }

  const combinedErrorMsg = errorMessages.join("; ");

  return (
    <motion.div
      layout
      variants={rowVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      transition={{ delay: index * 0.02, duration: 0.25, ease: "easeOut" }}
      className={cn(
        "grid grid-cols-[1fr_1fr_160px_100px_80px] gap-3 items-center px-4 py-2.5 border-b border-[var(--color-border-light)] transition-colors duration-200",
        hasError && "bg-red-50/60",
        !hasError && "hover:bg-[var(--color-bg-secondary)]/60"
      )}
    >
      {/* ── Source Column Cell ── */}
      <div className="min-w-0">
        {isMandatoryTab ? (
          /* Mandatory tab: source is always a dropdown */
          <>
            <SearchableSelect
              value={row.sourceColumn}
              options={sourceOptions}
              onChange={onFieldChange}
              placeholder="Select source column..."
              hasError={hasError}
              preserveOptionsOrder
            />
            {hasError && combinedErrorMsg && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="text-[10px] text-red-600 mt-1 leading-tight"
              >
                {combinedErrorMsg}
              </motion.p>
            )}
          </>
        ) : (
          /* Additional tab: source is static text */
          <span className="text-sm font-medium text-[var(--color-text-primary)] truncate block">
            {row.sourceColumn}
          </span>
        )}
      </div>

      {/* ── Target Column Cell ── */}
      <div className="min-w-0">
        {isMandatoryTab ? (
          /* Mandatory tab: target is locked with tooltip */
          <TargetColumnTooltip description={targetField?.definition}>
            <div className="flex flex-col cursor-help">
              <span className="text-sm font-medium text-[var(--color-text-primary)] truncate flex items-center gap-1.5">
                {targetDisplayName}
                <svg
                  className="w-3 h-3 text-[var(--color-text-tertiary)] shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </span>
              {targetField && (
                <span className="text-[10px] text-[var(--color-text-tertiary)] truncate">
                  {targetField.definition}
                </span>
              )}
            </div>
          </TargetColumnTooltip>
        ) : (
          /* Additional tab: target is a dropdown */
          <>
            <SearchableSelect
              value={targetDisplayName}
              options={targetOptions}
              onChange={onFieldChange}
              placeholder="Select target field..."
              hasError={hasError && !row.targetFieldId}
              preserveOptionsOrder
            />
            {hasError && combinedErrorMsg && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="text-[10px] text-red-600 mt-1 leading-tight"
              >
                {combinedErrorMsg}
              </motion.p>
            )}
          </>
        )}
      </div>

      {/* Status Badge */}
      <MappingStatusBadge
        status={row.status}
        appearance={
          row.status === "ML" && Math.round(row.confidence * 100) === 100
            ? "known_mapping"
            : undefined
        }
        overrideLabel={(() => {
          if (!isMandatoryTab && row.status === "needs_match") return "No Match Found";
          if (row.status === "ML" && Math.round(row.confidence * 100) === 100) {
            return "Exact match";
          }
          return undefined;
        })()}
      />

      {/* Confidence */}
      <div className="text-center">
        {row.status === "LLM" || row.status === "needs_match" ? (
          <span className="text-xs text-[var(--color-text-tertiary)]">--</span>
        ) : row.confidence > 0 ? (
          <span
            className={cn(
              "text-sm font-mono tabular-nums",
              row.confidence >= 0.85
                ? "text-emerald-600"
                : row.confidence >= 0.7
                  ? "text-amber-600"
                  : "text-red-600"
            )}
          >
            {Math.round(row.confidence * 100)}%
          </span>
        ) : (
          <span className="text-xs text-[var(--color-text-tertiary)]">--</span>
        )}
      </div>

      {/* Approve Checkbox */}
      <div className="flex justify-center">
        <div
          className="relative"
          onMouseEnter={() => isCheckboxDisabled && setShowCheckboxTip(true)}
          onMouseLeave={() => setShowCheckboxTip(false)}
        >
          <motion.button
            type="button"
            whileTap={isCheckboxDisabled ? undefined : { scale: 0.85 }}
            onClick={isCheckboxDisabled ? undefined : onToggleApproval}
            disabled={isCheckboxDisabled}
            className={cn(
              "w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all duration-150",
              isMandatoryLocked
                ? "bg-[var(--color-brand-primary)] border-[var(--color-brand-primary)] opacity-70 cursor-default"
                : row.approved
                  ? "bg-[var(--color-brand-primary)] border-[var(--color-brand-primary)]"
                  : "border-[var(--color-border)] hover:border-[var(--color-brand-primary)]",
              !isMandatoryLocked && isCheckboxDisabled && "opacity-30 cursor-not-allowed",
              !isMandatoryLocked && isCheckboxDisabled && hasError && "border-red-300"
            )}
            aria-label={
              isMandatoryLocked
                ? "Mandatory field (always included)"
                : isCheckboxDisabled
                  ? disabledReason
                  : row.approved
                    ? "Unapprove mapping"
                    : "Approve mapping"
            }
          >
            {(row.approved || isMandatoryLocked) &&
              (isMandatoryLocked ? (
                /* Lock icon for mandatory rows */
                <svg
                  className="w-3 h-3 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              ) : (
                /* Checkmark for user-approved rows */
                <svg
                  className="w-3.5 h-3.5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ))}
          </motion.button>

          <AnimatePresence>
            {showCheckboxTip && disabledReason && (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                transition={{ duration: 0.12 }}
                className="absolute bottom-full mb-2 right-0 z-50 w-56 p-2.5 rounded-lg bg-[var(--color-nav-bg)] text-white text-[11px] leading-snug shadow-xl pointer-events-none"
              >
                {disabledReason}
                <div className="absolute top-full right-3 w-2 h-2 rotate-45 bg-[var(--color-nav-bg)]" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}

// ── Error Banner ───────────────────────────────────────────

function ErrorBanner({ errors }: { errors: MappingError[] }) {
  if (errors.length === 0) return null;
  return (
    <div className="mb-3 space-y-1.5">
      {errors.map((err, i) => (
        <motion.div
          key={`${err.type}-${err.value ?? ""}-${i}`}
          initial={{ opacity: 0, y: -8, height: 0 }}
          animate={{ opacity: 1, y: 0, height: "auto" }}
          exit={{ opacity: 0, y: -8, height: 0 }}
          transition={{ duration: 0.2 }}
        >
          <AlertMessage
            variant={
              err.type === "missing_mandatory" || err.type === "duplicate_column"
                ? "error"
                : "warning"
            }
            message={err.message}
          />
        </motion.div>
      ))}
    </div>
  );
}

// ── Floating Action Bar ────────────────────────────────────

function FloatingActionBar({
  step,
  canContinue,
  canSave,
  isSaving,
  mandatoryStats,
  onContinue,
  onSave,
  onBack,
  onPreview,
}: {
  step: number;
  canContinue: boolean;
  canSave: boolean;
  isSaving?: boolean;
  mandatoryStats: { satisfied: number; total: number };
  onContinue: () => void;
  onSave: () => void;
  onBack?: () => void;
  onPreview?: () => void;
}) {
  const allMet = mandatoryStats.satisfied === mandatoryStats.total;
  const isMandatory = step === 1;

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.3, type: "spring", stiffness: 300, damping: 30 }}
      className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-[var(--color-border-light)] shadow-2xl"
    >
      <div className="mx-auto max-w-7xl px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span
            className={cn(
              "w-2 h-2 rounded-full",
              allMet ? "bg-emerald-500" : "bg-amber-500 animate-pulse"
            )}
          />
          <span className="text-sm font-medium text-[var(--color-text-primary)]">
            {mandatoryStats.satisfied} / {mandatoryStats.total} mandatory targets matched
          </span>
        </div>
        <div className="flex items-center gap-3">
          {onPreview ? (
            <Button variant="ghost" size="sm" onClick={onPreview} className="gap-1.5">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.8}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              File Preview
            </Button>
          ) : null}

          {onBack && (
            <Button variant="secondary" size="sm" onClick={onBack}>
              Back to Mandatory
            </Button>
          )}

          {isMandatory ? (
            /* Mandatory tab: Continue to Additional Fields */
            <Button
              variant="primary"
              size="sm"
              onClick={onContinue}
              disabled={!canContinue}
              className={cn(
                "min-w-[220px] transition-all duration-300",
                canContinue && "ring-2 ring-[var(--color-brand-primary)]/30 hover:ring-4"
              )}
            >
              Continue to Additional Fields
              <svg
                className="w-4 h-4 ml-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Button>
          ) : (
            /* Additional tab: Save Mappings */
            <Button
              variant="primary"
              size="sm"
              onClick={onSave}
              disabled={!canSave || isSaving}
              className={cn(
                "min-w-[160px] transition-all duration-300",
                canSave && !isSaving && "ring-2 ring-[var(--color-brand-primary)]/30 hover:ring-4"
              )}
            >
              {isSaving ? "Saving…" : "Save Mappings"}
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ── Main Page Component ────────────────────────────────────

function RegionAdvanceToast({ message }: { message: string }) {
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="fixed top-6 right-6 z-50 px-6 py-4 rounded-xl bg-[var(--color-nav-bg)] text-white shadow-2xl flex items-center gap-3"
      >
        <span className="w-6 h-6 rounded-full bg-[var(--color-brand-primary)] flex items-center justify-center">
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={3}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </span>
        <span className="text-sm font-medium">{message}</span>
      </motion.div>
    </AnimatePresence>
  );
}

function formatRegionAdvanceMessage(
  notice: { completedIndex: number; nextIndex: number },
  regionCount: number
): string {
  return `Table ${notice.completedIndex + 1} mapped. Loading table ${notice.nextIndex + 1} of ${regionCount}…`;
}

/** Emitted when a region (or whole file) mapping apply completes. */
export interface MappingWorkbenchFileMappedEvent {
  fileId: string;
  savedMappings: KnownMapping[];
  /** Primary/last DQX run (back-compat for single-job consumers). */
  dqx: MappingWorkbenchDqxRun | null;
  /** One entry per mapped table when multi-region apply triggered DQX per region. */
  dqxRuns?: MappingWorkbenchDqxRun[];
}

export interface MappingWorkbenchProps {
  onExitToBoard: () => void;
  onContinueToDataQuality: () => void;
  /**
   * Board host's completion hook. When provided, the workbench reports the
   * mapped file upward and exits to the board; the host owns board state and
   * DQ polling. Without it the legacy continue-to-DQ-view flow runs.
   */
  onFileMapped?: (event: MappingWorkbenchFileMappedEvent) => void;
  /** Prior per-table DQX runs (resume / board restore) merged into the session ref. */
  initialDqxRuns?: MappingWorkbenchDqxRun[];
  /**
   * Pad banner/content instead of the dialog scroll shell so sticky tabs
   * can sit at top-0 with no gap above Mandatory / Additional Fields.
   */
  dialogInset?: boolean;
}

export function MappingWorkbench({
  onExitToBoard,
  onContinueToDataQuality,
  onFileMapped,
  initialDqxRuns = [],
  dialogInset = false,
}: MappingWorkbenchProps) {
  const {
    isMultiRegion,
    documentId,
    hydrateMappingProgress,
    currentRegionIndex,
    mappingRegions,
    regionAdvanceNotice,
    setRegionAdvanceNotice,
  } = useUpload();
  const [resumeReady, setResumeReady] = useState(!isMultiRegion);
  const processing = useProcessing();
  const wasProcessingRef = useRef(false);
  /** Survives Inner remounts across region advances (Inner is keyed by region index). */
  const regionDqxRunsRef = useRef<MappingWorkbenchDqxRun[]>([]);

  useEffect(() => {
    regionDqxRunsRef.current = [...initialDqxRuns];
  }, [documentId, initialDqxRuns]);

  const duplicateRegionIds = useMemo(() => {
    if (!isMultiRegion) return [] as string[];
    const counts = new Map<string, number>();
    for (const region of mappingRegions) {
      const id = region.regionId.trim();
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return [...counts.entries()].filter(([, count]) => count > 1).map(([id]) => id);
  }, [isMultiRegion, mappingRegions]);

  useEffect(() => {
    if (!isMultiRegion || !documentId) {
      setResumeReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const progress = await fetchMappingProgress(documentId);
        if (!cancelled && progress.completedRegionIds.length > 0) {
          hydrateMappingProgress(progress.completedRegionIds);
        }
      } finally {
        if (!cancelled) setResumeReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [documentId, hydrateMappingProgress, isMultiRegion]);

  useEffect(() => {
    if (processing.loading) {
      wasProcessingRef.current = true;
      return;
    }
    if (wasProcessingRef.current && regionAdvanceNotice) {
      setRegionAdvanceNotice(null);
      wasProcessingRef.current = false;
    }
  }, [processing.loading, regionAdvanceNotice, setRegionAdvanceNotice]);

  const loadingMessage = regionAdvanceNotice
    ? formatRegionAdvanceMessage(regionAdvanceNotice, mappingRegions.length)
    : isMultiRegion
      ? `Processing table ${currentRegionIndex + 1} of ${mappingRegions.length}…`
      : "Processing file through the mapping pipeline…";

  const advanceToastMessage = regionAdvanceNotice
    ? formatRegionAdvanceMessage(regionAdvanceNotice, mappingRegions.length)
    : null;

  if (!resumeReady || processing.loading) {
    return (
      <>
        {advanceToastMessage && <RegionAdvanceToast message={advanceToastMessage} />}
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 animate-fade-in">
          <Spinner size="lg" />
          <p className="text-sm text-[var(--color-text-secondary)]">{loadingMessage}</p>
        </div>
      </>
    );
  }

  if (processing.error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <AlertMessage variant="error" message={`Processing failed: ${processing.error}`} />
        <Button onClick={() => window.location.reload()}>Try Again</Button>
      </div>
    );
  }

  if (duplicateRegionIds.length > 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-6">
        <AlertMessage
          variant="error"
          message={`This file has ${duplicateRegionIds.length} mapping table(s) sharing the same region id (${duplicateRegionIds.join(", ")}). Each table needs a distinct region before data quality can run per table. Re-run extraction or adjust confirm-structure selection.`}
        />
        <Button onClick={onExitToBoard}>Back to Workspace</Button>
      </div>
    );
  }

  return (
    <MappingWorkbenchInner
      key={isMultiRegion ? `region-${currentRegionIndex}` : "single"}
      initialRows={processing.rows}
      sourceColumns={processing.sourceColumns}
      duplicateColumns={processing.duplicateColumns}
      schema={processing.schema}
      dialogInset={dialogInset}
      onExitToBoard={onExitToBoard}
      onContinueToDataQuality={onContinueToDataQuality}
      onFileMapped={onFileMapped}
      regionDqxRunsRef={regionDqxRunsRef}
    />
  );
}

function MappingWorkbenchInner({
  initialRows,
  sourceColumns,
  duplicateColumns,
  schema,
  dialogInset = false,
  onExitToBoard,
  onContinueToDataQuality,
  onFileMapped,
  regionDqxRunsRef,
}: {
  initialRows: MappingRow[];
  sourceColumns: string[];
  duplicateColumns: string[];
  schema: SchemaLookups;
  regionDqxRunsRef: MutableRefObject<MappingWorkbenchDqxRun[]>;
} & MappingWorkbenchProps) {
  const {
    fileName,
    worksheets,
    mappingSelection,
    inputColumns,
    documentId,
    schemaId,
    schemaName,
    saveMappings,
    savedMappings,
    lockRegionAndAdvance,
    isMultiRegion,
    mappingRegions,
    currentRegionIndex,
    activeRegion,
    requiresReportType,
    setDqxRunInfo,
    setRegionAdvanceNotice,
  } = useUpload();
  const { track, startTimer } = useTelemetry();
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [applyError, setApplyError] = useState("");

  // Derive schema constants
  const MANDATORY_TARGET_IDS = schema.mandatoryFieldIds;
  const effectiveSourceColumns = sourceColumns.length > 0 ? sourceColumns : inputColumns;

  const [step, setStep] = useState(1);
  const [saved, setSaved] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Track time spent on the mapping page
  const pageLoadTimer = useRef(startTimer());

  // ── Single unified state for all rows ──────────────────────
  const unified = useMappingState(initialRows, schema.getFieldName);

  const {
    approveAll: unifiedApproveAll,
    deselectAll: unifiedDeselectAll,
    setSort: unifiedSetSort,
    updateRow: unifiedUpdateRow,
    toggleApproval: unifiedToggleApproval,
    errorRowIds,
    errorsByRowId,
    rawRows,
    rows: sortedRows,
    sort: unifiedSort,
    errors: unifiedErrors,
  } = unified;

  // ── Derived views per tab ──────────────────────────────────

  // Mandatory tab: rows targeting mandatory fields, or target_to_source (unmatched mandatory)
  const mandatoryRows = useMemo(() => {
    const rows = sortedRows.filter(
      (r) => MANDATORY_TARGET_IDS.has(r.targetFieldId) || r.direction === "target_to_source"
    );

    // Column sort: if user has triggered column sort, preserve sortedRows order
    if (unifiedSort.field) {
      return rows;
    }

    // Default sorting: if no column sort triggered by user, order by errors first, then by mapping status, then alphabetical by target name
    return [...rows].sort((a, b) => {
      const aHasError = errorRowIds.has(a.id) ? 0 : 1;
      const bHasError = errorRowIds.has(b.id) ? 0 : 1;
      if (aHasError !== bHasError) return aHasError - bHasError;

      const aStatus = STATUS_SORT_ORDER[a.status] ?? 99;
      const bStatus = STATUS_SORT_ORDER[b.status] ?? 99;
      if (aStatus !== bStatus) return aStatus - bStatus;

      const aName = schema.getFieldName(a.targetFieldId) || "\uffff";
      const bName = schema.getFieldName(b.targetFieldId) || "\uffff";
      return aName.localeCompare(bName);
    });
  }, [sortedRows, errorRowIds, MANDATORY_TARGET_IDS, schema, unifiedSort.field]);

  // Additional tab: rows NOT targeting mandatory fields.
  const additionalRows = useMemo(
    () =>
      sortedRows.filter(
        (r) => !MANDATORY_TARGET_IDS.has(r.targetFieldId) && r.direction !== "target_to_source"
      ),
    [sortedRows, MANDATORY_TARGET_IDS]
  );

  const isMandatoryTab = step === 1;
  const currentRows = isMandatoryTab ? mandatoryRows : additionalRows;

  const currentViewStats = useMemo(() => {
    const rows = currentRows;
    const approved = rows.filter((r) => r.approved).length;
    const approvable = rows.filter((r) => {
      if (errorRowIds.has(r.id)) return false;
      return !!r.sourceColumn && !!r.targetFieldId;
    }).length;
    return { total: rows.length, approved, approvable };
  }, [currentRows, errorRowIds]);

  // IDs of rows targeting mandatory fields -- these are always approved when complete
  const mandatoryRowIds = useMemo(() => {
    const ids = new Set<string>();
    for (const r of rawRows) {
      if (MANDATORY_TARGET_IDS.has(r.targetFieldId) || r.direction === "target_to_source") {
        ids.add(r.id);
      }
    }
    return ids;
  }, [rawRows, MANDATORY_TARGET_IDS]);

  const scopedApproveAll = useCallback(
    () => unifiedApproveAll(errorRowIds),
    [unifiedApproveAll, errorRowIds]
  );
  const scopedDeselectAll = useCallback(
    () => unifiedDeselectAll(mandatoryRowIds),
    [unifiedDeselectAll, mandatoryRowIds]
  );

  // ── Cross-step mandatory target coverage ─────────────────
  const satisfiedMandatory = useMemo(() => {
    const all = new Set<string>();
    for (const r of rawRows) {
      if (r.targetFieldId && MANDATORY_TARGET_IDS.has(r.targetFieldId)) {
        // Mandatory target is satisfied only when it has BOTH source and target
        if (!r.sourceColumn) continue;
        all.add(r.targetFieldId);
      }
    }
    return all;
  }, [rawRows, MANDATORY_TARGET_IDS]);

  const mandatoryStatsGlobal = useMemo(
    () => ({ satisfied: satisfiedMandatory.size, total: schema.mandatoryFields.length }),
    [satisfiedMandatory, schema.mandatoryFields.length]
  );

  const allMandatorySatisfied = satisfiedMandatory.size === schema.mandatoryFields.length;

  const missingMandatoryError = useMemo<MappingError | null>(() => {
    const missing = schema.mandatoryFields.filter((f) => !satisfiedMandatory.has(f.id));
    if (missing.length === 0) return null;
    const names = missing.map((f) => f.fieldName);
    return {
      type: "missing_mandatory",
      rowIds: [],
      message: `${missing.length} mandatory target${missing.length > 1 ? "s" : ""} still need a match: ${names.join(", ")}`,
    };
  }, [satisfiedMandatory, schema.mandatoryFields]);

  const duplicateColumnError = useMemo<MappingError | null>(() => {
    if (duplicateColumns.length === 0) return null;
    return {
      type: "duplicate_column",
      rowIds: [],
      message: `${duplicateColumns.length} duplicate column${duplicateColumns.length > 1 ? "s" : ""} detected: ${duplicateColumns.join(", ")}`,
    };
  }, [duplicateColumns]);

  const relevantErrors = useMemo(() => {
    const errs: MappingError[] = [];
    if (duplicateColumnError) errs.push(duplicateColumnError);
    if (step === 1 && missingMandatoryError) errs.push(missingMandatoryError);
    // Show errors that involve rows visible in the current tab
    const currentRowIds = new Set(currentRows.map((r) => r.id));
    for (const err of unifiedErrors) {
      if (err.rowIds.some((id) => currentRowIds.has(id))) {
        errs.push(err);
      }
    }
    return errs;
  }, [step, missingMandatoryError, unifiedErrors, currentRows, duplicateColumnError]);

  // ── Tab switch ───────────────────────────────────────────
  const handleTabSwitch = useCallback(
    (newStep: number) => {
      if (newStep === step) return;
      if (newStep === 2 && !allMandatorySatisfied) return;
      setStep(newStep);
      unifiedSetSort({ field: null, direction: "asc" });
      track("mapping_tab_switched", {
        tab: newStep === 1 ? "mandatory" : "additional",
        mandatorySatisfied: satisfiedMandatory.size,
        mandatoryTotal: schema.mandatoryFields.length,
      });
    },
    [
      step,
      allMandatorySatisfied,
      satisfiedMandatory.size,
      schema.mandatoryFields.length,
      unifiedSetSort,
      track,
    ]
  );

  // ── Sort handler ─────────────────────────────────────────
  const handleSort = useCallback(
    (field: SortField) => {
      unifiedSetSort((prev) => {
        let direction: "asc" | "desc";

        if (prev.field !== field) {
          direction = "asc";
        } else {
          direction = prev.direction === "asc" ? "desc" : "asc";
        }
        track("mapping_sort_changed", { field: field ?? "", direction });
        return { field, direction };
      });
    },
    [unifiedSetSort, track]
  );

  // ── Select all / Deselect all ────────────────────────────
  const handleSelectAll = useCallback(() => {
    scopedApproveAll();
    track("mapping_select_all", {
      tab: isMandatoryTab ? "mandatory" : "additional",
      approvableCount: currentViewStats.approvable,
    });
  }, [scopedApproveAll, isMandatoryTab, currentViewStats.approvable, track]);

  const handleDeselectAll = useCallback(() => {
    scopedDeselectAll();
    track("mapping_deselect_all", {
      tab: isMandatoryTab ? "mandatory" : "additional",
      previouslyApproved: currentViewStats.approved,
    });
  }, [scopedDeselectAll, isMandatoryTab, currentViewStats.approved, track]);

  // ── Field change handlers ────────────────────────────────
  const handleFieldChange = useCallback(
    (row: MappingRow, option: SelectOption | null) => {
      const selectedValue = option?.value ?? "";
      const selectedConfidence = option?.confidence ?? 0;
      const previouslyMapped = !!row.sourceColumn && !!row.targetFieldId;

      if (isMandatoryTab) {
        // Mandatory tab: always updating source column
        track("mapping_field_changed", {
          tab: "mandatory",
          field: "sourceColumn",
          tier: row.status,
          confidence: Math.round(row.confidence * 100),
          isMandatory: true,
          cleared: !selectedValue,
          previouslyMapped,
        });
        unifiedUpdateRow(row.id, {
          sourceColumn: selectedValue || "",
          approved: !!selectedValue, // selecting a source column counts as approval; clearing it removes it
          confidence: selectedConfidence,
        });
      } else {
        // Additional tab: always updating target field
        track("mapping_field_changed", {
          tab: "additional",
          field: "targetFieldId",
          tier: row.status,
          confidence: Math.round(row.confidence * 100),
          isMandatory: false,
          cleared: !selectedValue,
          previouslyMapped,
        });
        if (!selectedValue) {
          unifiedUpdateRow(row.id, { targetFieldId: "", approved: false });
          return;
        }
        const match = schema.byName.get(selectedValue);
        if (match) {
          unifiedUpdateRow(row.id, {
            targetFieldId: match.id,
            approved: false,
            confidence: selectedConfidence,
          });
        }
      }
    },
    [isMandatoryTab, unifiedUpdateRow, schema.byName, track]
  );

  // ── Approval toggle ──────────────────────────────────────
  const handleToggleApproval = useCallback(
    (row: MappingRow) => {
      unifiedToggleApproval(row.id, errorRowIds, mandatoryRowIds);
      track("mapping_approval_toggled", {
        approved: !row.approved,
        tier: row.status,
        confidence: Math.round(row.confidence * 100),
        isMandatory: MANDATORY_TARGET_IDS.has(row.targetFieldId),
      });
    },
    [unifiedToggleApproval, errorRowIds, mandatoryRowIds, MANDATORY_TARGET_IDS, track]
  );

  // ── Continue to Additional Fields ────────────────────────
  const handleContinue = useCallback(() => {
    track("mapping_continue_to_additional", {
      mandatorySatisfied: satisfiedMandatory.size,
      mandatoryTotal: schema.mandatoryFields.length,
    });
    setStep(2);
    unifiedSetSort({ field: null, direction: "asc" });
  }, [satisfiedMandatory.size, schema.mandatoryFields.length, unifiedSetSort, track]);

  // ── Save ─────────────────────────────────────────────────
  const canSave =
    allMandatorySatisfied &&
    (!isMultiRegion || !requiresReportType || Boolean(activeRegion?.reportType?.trim()));

  const handleSave = useCallback(async () => {
    if (!canSave || isSaving) return;

    const schemaIdValue = schema.fields[0]?.schemaId ?? schemaId ?? "";

    const isMandatoryMappingRow = (r: MappingRow) =>
      MANDATORY_TARGET_IDS.has(r.targetFieldId) || r.direction === "target_to_source";

    const toSave = rawRows.filter((r) => {
      if (!r.sourceColumn || !r.targetFieldId || errorRowIds.has(r.id)) return false;
      if (isMandatoryMappingRow(r)) return true;
      return r.approved;
    });

    const totalApproved = rawRows.filter((r) => r.approved).length;
    const durationMs = pageLoadTimer.current();
    const scopedTarget = resolveExtractionMappingTarget({
      isMultiRegion,
      activeRegionId: activeRegion?.regionId,
      activeRegionHeaders: activeRegion?.headers,
      worksheets,
      mappingSelection,
      inputColumns,
    });
    const regionId = isMultiRegion ? activeRegion?.regionId : scopedTarget?.regionId;
    const regionReportType = activeRegion?.reportType ?? null;
    const isLastRegion = isMultiRegion && currentRegionIndex === mappingRegions.length - 1;

    track(
      "mappings_saved",
      {
        mandatorySatisfied: satisfiedMandatory.size,
        mandatoryTotal: schema.mandatoryFields.length,
        totalApproved,
        knownMappingsSaved: toSave.length,
        activeTab: "additional",
        multiRegion: isMultiRegion,
        regionId,
      },
      { durationMs }
    );

    const knownMappings: KnownMapping[] = toSave.map((row, i) => ({
      mappingId: `saved-${regionId ?? "single"}-${i + 1}`,
      sourceField: row.sourceColumn,
      targetField: schema.getFieldName(row.targetFieldId) ?? row.targetFieldId,
      targetFieldId: row.targetFieldId,
      schemaId: schemaIdValue,
      schemaName: schemaName || schemaIdValue,
      approvedBy: "User",
      createdAt: new Date().toISOString(),
      regionId,
      reportType: regionReportType,
    }));

    if (isMultiRegion && regionId && documentId) {
      setIsSaving(true);
      setApplyError("");
      try {
        const applyResult = await applyMappings({
          fileId: documentId,
          schemaId: schemaIdValue,
          reportType: regionReportType,
          mappings: toSave.map((row) => ({
            source_col: row.sourceColumn,
            target_col: row.targetFieldId,
            region_id: regionId,
          })),
          regionId,
          append: currentRegionIndex > 0,
          triggerDqx: true,
          multiRegion: true,
        });
        const regionRun = requireDqxRunFromApply(regionId, applyResult);
        regionDqxRunsRef.current = [...regionDqxRunsRef.current, regionRun];

        if (!isLastRegion) {
          setRegionAdvanceNotice({
            completedIndex: currentRegionIndex,
            nextIndex: currentRegionIndex + 1,
          });
        }
        lockRegionAndAdvance(currentRegionIndex, knownMappings);
        if (isLastRegion) {
          setSaved(true);
          const { dqx, dqxRuns } = buildFileMappedDqxPayload(regionDqxRunsRef.current);
          if (onFileMapped && documentId.trim()) {
            const allMappings = [...savedMappings, ...knownMappings];
            void saveMappingSnapshot(documentId, allMappings).catch((err) =>
              console.error("Failed to save mapping snapshot:", err)
            );
            onFileMapped({
              fileId: documentId,
              savedMappings: allMappings,
              dqx,
              dqxRuns,
            });
            onExitToBoard();
          } else {
            setDqxRunInfo({
              runId: regionRun.runId,
              jobId: regionRun.jobId,
              message: regionRun.message ?? "",
            });
            onContinueToDataQuality();
          }
        } else {
          setStep(1);
          unifiedSetSort({ field: null, direction: "asc" });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.error("Failed to apply region mappings:", err);
        setApplyError(message);
      } finally {
        setIsSaving(false);
      }
      return;
    }

    saveMappings(knownMappings);

    // Navigate immediately — persistence runs in the background (DQX runs from the board)
    setSaved(true);
    if (onFileMapped && documentId.trim()) {
      void saveMappingSnapshot(documentId, knownMappings).catch((err) =>
        console.error("Failed to save mapping snapshot:", err)
      );
      onFileMapped({ fileId: documentId, savedMappings: knownMappings, dqx: null });
      onExitToBoard();
    } else {
      onContinueToDataQuality();
    }

    // Persist target_field as schema field_id — the mapping pipeline filters and joins on field_id.
    // Pass documentId as fileId so the backend can stamp provider_id from the uploading file
    // (critical for shared presets like lloyds_5_2_v1 where schema.provider_id is NULL).
    createKnownMappings(
      toSave.map((row) => ({
        schemaId: schemaIdValue,
        sourceField: row.sourceColumn,
        targetField: row.targetFieldId,
        approvedBy: "User",
        ...(documentId ? { fileId: documentId } : {}),
      }))
    ).catch((err) => console.error("Failed to save known mappings:", err));
  }, [
    canSave,
    isSaving,
    rawRows,
    errorRowIds,
    satisfiedMandatory.size,
    saveMappings,
    savedMappings,
    documentId,
    onExitToBoard,
    onContinueToDataQuality,
    onFileMapped,
    track,
    schema,
    schemaId,
    schemaName,
    MANDATORY_TARGET_IDS,
    isMultiRegion,
    activeRegion,
    currentRegionIndex,
    mappingRegions,
    worksheets,
    mappingSelection,
    inputColumns,
    lockRegionAndAdvance,
    setDqxRunInfo,
    setRegionAdvanceNotice,
    unifiedSetSort,
    regionDqxRunsRef,
  ]);

  // ── File Preview ─────────────────────────────────────────
  const handlePreviewOpen = useCallback(() => {
    setPreviewOpen(true);
    track("mapping_file_preview_opened", {
      tab: isMandatoryTab ? "mandatory" : "additional",
    });
  }, [isMandatoryTab, track]);

  // ── Sticky tabs ref ──────────────────────────────────────
  const tabsRef = useRef<HTMLDivElement>(null);
  const [isTabsSticky, setIsTabsSticky] = useState(false);

  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setIsTabsSticky(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: "-1px 0px 0px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const activeTableLabel = useMemo(() => {
    if (!isMultiRegion || !activeRegion) return null;
    const labelParts = mappingRegionTableLabelParts(activeRegion, currentRegionIndex + 1);
    return t(mappingRegionTableLabelKey(labelParts), labelParts);
  }, [isMultiRegion, activeRegion, currentRegionIndex, t]);

  return (
    <>
      {/* Banner / intro padded when in dialog — scroll shell has no top padding so sticky tabs sit flush. */}
      <div className={cn(dialogInset && "px-6 pt-6")}>
        {/* Compact Page Banner */}
        <PageBanner
          title="Column Mapping Review"
          subtitle={
            isMultiRegion && activeTableLabel
              ? `Table ${currentRegionIndex + 1} of ${mappingRegions.length}: ${activeTableLabel}`
              : fileName
                ? `Reviewing: ${fileName}`
                : "Map source columns to the target schema"
          }
          className="!h-28 !mb-4"
        />

        {isMultiRegion && (
          <RegionMappingStepper regions={mappingRegions} currentIndex={currentRegionIndex} />
        )}

        {/* Compact section description */}
        <div className="mb-3 animate-fade-in-up stagger-2">
          <p className="text-sm text-[var(--color-text-secondary)] max-w-3xl">
            {isMandatoryTab
              ? `The selected schema has ${schema.mandatoryFields.length} mandatory target fields. For each mandatory target, select the source column from your file that maps to it. Approve the matches to continue.`
              : `Review and approve the additional column mappings below. These are the non-mandatory fields from your source file. Select target fields or leave unmapped columns empty.`}
          </p>
        </div>

        {/* Errors */}
        <ErrorBanner errors={relevantErrors} />
      </div>

      {/* Sentinel for sticky detection */}
      <div ref={tabsRef} className="h-0" />

      {/* Tab bar + Table — sticky top-0; horizontal inset only when dialogInset */}
      <div
        className={cn(
          "rounded-xl border border-[var(--color-border-light)] overflow-visible mb-6",
          dialogInset && "mx-6"
        )}
      >
        {/* Tab Bar -- sticky */}
        <div
          className={cn(
            "sticky top-0 z-20 flex border-b border-[var(--color-border-light)] bg-white rounded-t-xl transition-shadow duration-200",
            isTabsSticky && "shadow-md"
          )}
        >
          {/* Mandatory Fields Tab */}
          <button
            type="button"
            onClick={() => handleTabSwitch(1)}
            className={cn(
              "flex items-center gap-2 px-5 py-3 text-sm font-medium transition-all duration-200 cursor-pointer border-b-2 -mb-px",
              step === 1
                ? "border-[var(--color-brand-primary)] text-[var(--color-brand-primary-dark)] bg-[var(--color-brand-primary)]/5"
                : "border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)]"
            )}
          >
            Mandatory Fields
            <span
              className={cn(
                "text-xs px-2 py-0.5 rounded-full font-medium",
                step === 1
                  ? "bg-[var(--color-brand-primary)]/15 text-[var(--color-brand-primary-dark)]"
                  : "bg-[var(--color-bg-tertiary)] text-[var(--color-text-tertiary)]"
              )}
            >
              {mandatoryRows.length}
            </span>
            {allMandatorySatisfied && (
              <svg
                className="w-4 h-4 text-emerald-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>

          {/* Additional Fields Tab */}
          <button
            type="button"
            onClick={() => {
              if (step !== 2) {
                unifiedSetSort({ field: null, direction: "asc" });
                setStep(2);
              }
            }}
            className={cn(
              "flex items-center gap-2 px-5 py-3 text-sm font-medium transition-all duration-200 border-b-2 -mb-px",
              step === 2
                ? "border-[var(--color-brand-primary)] text-[var(--color-brand-primary-dark)] bg-[var(--color-brand-primary)]/5 cursor-pointer"
                : "border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] cursor-pointer"
            )}
          >
            Additional Fields
            <span
              className={cn(
                "text-xs px-2 py-0.5 rounded-full font-medium",
                step === 2
                  ? "bg-[var(--color-brand-primary)]/15 text-[var(--color-brand-primary-dark)]"
                  : "bg-[var(--color-bg-tertiary)] text-[var(--color-text-tertiary)]"
              )}
            >
              {additionalRows.length}
            </span>
          </button>
        </div>

        {/* Table Content */}
        <div key={step} className="animate-fade-in" style={{ animationDuration: "0.2s" }}>
          <TableHeader
            sort={unifiedSort}
            onSort={handleSort}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            approvedCount={currentViewStats.approved}
            approvableCount={currentViewStats.approvable}
            isMandatoryTab={isMandatoryTab}
          />

          <div>
            {currentRows.map((row, i) => {
              const hasError = errorRowIds.has(row.id);
              const errorMsgs = errorsByRowId.get(row.id) ?? [];

              return (
                <MappingRowComponent
                  key={row.id}
                  row={row}
                  index={i}
                  isMandatoryTab={isMandatoryTab}
                  sourceOptions={isMandatoryTab ? buildSourceOptions(effectiveSourceColumns) : []}
                  targetOptions={
                    isMandatoryTab ? [] : buildAdditionalTargetOptions(row, schema.additionalFields)
                  }
                  schema={schema}
                  hasError={hasError}
                  errorMessages={errorMsgs}
                  onFieldChange={(option) => handleFieldChange(row, option)}
                  onToggleApproval={() => handleToggleApproval(row)}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Extra bottom padding */}
      <div className="h-48" />

      {/* Success Toast */}
      <AnimatePresence>
        {saved && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed top-6 right-6 z-50 px-6 py-4 rounded-xl bg-[var(--color-nav-bg)] text-white shadow-2xl flex items-center gap-3"
          >
            <span className="w-6 h-6 rounded-full bg-[var(--color-brand-primary)] flex items-center justify-center">
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={3}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </span>
            <span className="text-sm font-medium">Mappings saved successfully</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Action Bar */}
      {applyError ? (
        <div className="fixed bottom-20 left-1/2 z-40 w-[min(36rem,calc(100%-2rem))] -translate-x-1/2">
          <AlertMessage variant="error" message={applyError} />
        </div>
      ) : null}
      <FloatingActionBar
        step={step}
        canContinue={true}
        canSave={canSave}
        isSaving={isSaving}
        mandatoryStats={mandatoryStatsGlobal}
        onContinue={handleContinue}
        onSave={handleSave}
        onBack={step > 1 ? () => handleTabSwitch(1) : undefined}
        onPreview={dialogInset ? undefined : handlePreviewOpen}
      />

      {/* File Preview Modal — standalone /mapping page only */}
      {!dialogInset ? (
        <FilePreviewModal
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          fileName={fileName || "Uploaded File"}
          worksheets={worksheets}
        />
      ) : null}
    </>
  );
}
